import moment from 'moment';
import schema from './qdrs-schema.json';
import {
  Child,
  ChildLifeStatus,
  DeaceasedYearOfArrivalAustralia,
  DeceasedDateOfDeathKnownRegistration,
  DeceasedParentIndicator,
  DeceasedPlaceOfBirthKnown,
  DeceasedPlaceOfDeathType,
  DeceasedSex,
  DeathDisposalType,
  DisposalOutsideMethod,
  MarriageAgeQualifier,
  MarriageType,
  QldBdmRecord,
  Relationship,
  Stillborn,
} from './form-enum-qld';

interface TransformContext {
  arrangement: any;
  working: any;
  contacts: Map<string, any>;
  record: QldBdmRecord;
}

type TransformHandler = (value: any, args: any[], context: TransformContext, key: string) => any;

export class NswToQldHelper {
  private readonly schemaProperties: Record<string, any>;
  private readonly transformHandlers: Record<string, TransformHandler>;
  private readonly genderMap: Map<string, DeceasedSex>;
  private readonly indigenousHeritageMap: Map<string, string>;
  private readonly dateOfPassingTypeMap: Map<string, DeceasedDateOfDeathKnownRegistration>;
  private readonly placeOfPassingTypeMap: Map<string, DeceasedPlaceOfDeathType>;

  constructor() {
    this.schemaProperties = (schema as any)?.items?.properties ?? {};
    this.transformHandlers = {};

    this.genderMap = new Map<string, DeceasedSex>([
      ['female', DeceasedSex.Female],
      ['male', DeceasedSex.Male],
      ['indeterminate', DeceasedSex.Indeterminate],
    ]);

    this.indigenousHeritageMap = new Map<string, string>([
      ['aboriginal', 'Aboriginal'],
      ['torres strait islander', 'Torres Strait Islander'],
      ['both aboriginal and torres strait islander', 'Both Aboriginal and Torres Strait Islander'],
      ['neither', 'Neither'],
      ['unknown', 'Unknown'],
    ]);

    this.dateOfPassingTypeMap = new Map<string, DeceasedDateOfDeathKnownRegistration>([
      ['on', DeceasedDateOfDeathKnownRegistration.Known],
      ['between', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['approx.', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['approx', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['on or after', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['on or about', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['sometime on or after', DeceasedDateOfDeathKnownRegistration.Approximately],
      ['unknown', DeceasedDateOfDeathKnownRegistration.DeceasedDateOfDeathUnknown],
    ]);

    this.placeOfPassingTypeMap = new Map<string, DeceasedPlaceOfDeathType>([
      ['nsw-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['vic-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['qld-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['act-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['sa-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['nt-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['wa-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['tas-hospital', DeceasedPlaceOfDeathType.Hospital],
      ['nsw-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['vic-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['qld-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['act-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['sa-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['nt-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['wa-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['tas-nursing-home', DeceasedPlaceOfDeathType.Agedcare],
      ['usual-residence', DeceasedPlaceOfDeathType.Anyother],
      ['none', DeceasedPlaceOfDeathType.UnknownPlace],
    ]);

    const transformNames = [
      'qldToStillBornEnum',
      'qdrsIsSingleName',
      'isTruthy',
      'conductorToQdrsGender',
      'conductorToQdrsGenderOtherTerm',
      'conductorToQdrsIndigenousHeritage',
      'conductorToQdrsDateOfPassingType',
      'toDateStr',
      'conductorToQdrsAgeByUnit',
      'conductorToQdrsAgeByUnitNumber',
      'conductorToQdrsAgeByUnitString',
      'conductorToQdrsPlaceOfDeathType',
      'conductorToQdrsPlaceOfDeath',
      'conductorToQdrsPlaceOfDeathIsResidence',
      'getAddressPlaceName',
      'join',
      'toBoolean',
      'conductorToQdrsPlaceOfBirthKnown',
      'isNotEqual',
      'conductorToQdrsYearOfArrivalToAustralia',
      'isAustralianPlaceOfBirth',
      'isNotAustralianPlaceOfBirth',
      'getLength',
      'toMarriageEnum',
      'isEqual',
      'returnValue',
      'isMarriageAgeKnown',
      'calculateMarriageAge',
      'toMarriageAgeQualifier',
      'contact',
      'contactOverview',
      'toLifeStatusEnum',
      'disposal',
      'get',
      'getDisposalPlace',
      'toDisposalTypeEnum',
      'toDisposalOutsideEnum',
      'isDisposalOutsideQld',
      'calculateAge',
      'toAgeUnit',
      'toParentTypeEnum',
      'log',
    ].filter((name) => typeof (this as any)[name] === 'function');

    transformNames.forEach((name) => {
      this.transformHandlers[name as string] = (
        value: any,
        args: any[],
        context: TransformContext,
        key: string
      ) => (this as any)[name].call(this, value, ...args, context, key);
    });
  }

  convert(arrangement: any): QldBdmRecord[] {
    const working = this.prepareWorkingCopy(arrangement);
    const context: TransformContext = {
      arrangement,
      working,
      contacts: this.buildContactIndex(arrangement),
      record: {} as QldBdmRecord,
    };

    const record: Record<string, unknown> = {};

    for (const [key, property] of Object.entries(this.schemaProperties)) {
      const value = this.resolveProperty(property, context, key);

      if (this.hasValue(value)) {
        record[key] = value;
      }
    }

    return [record as QldBdmRecord];
  }

  private prepareWorkingCopy(arrangement: any): any {
    const working: any = { ...arrangement };

    if (!working.children && working.family?.children) {
      working.children = working.family.children;
    }

    if (!working.parents && working.family?.parents) {
      working.parents = working.family.parents;
    }

    return working;
  }

  private buildContactIndex(arrangement: any): Map<string, any> {
    const contacts = new Map<string, any>();

    const register = (contact: any) => {
      if (contact && contact.id) {
        contacts.set(contact.id, contact);
      }
    };

    (arrangement?.contacts ?? []).forEach(register);

    const keyContacts = [
      arrangement?.firstCall?.informant,
      arrangement?.keyContacts?.informant,
      arrangement?.keyContacts?.nextOfKin,
      arrangement?.keyContacts?.executor,
    ];

    keyContacts.forEach((contact) => {
      if (contact?.id && !contacts.has(contact.id)) {
        contacts.set(contact.id, contact);
      }
    });

    return contacts;
  }

  private resolveProperty(property: any, context: TransformContext, key: string): any {
    if (!property) {
      return undefined;
    }

    if (property.type === 'array' && property.items?.properties) {
      return this.resolveArrayProperty(property.items.properties, context);
    }

    if (property.type === 'object' && property.properties) {
      const result: Record<string, unknown> = {};

      for (const [childKey, childProperty] of Object.entries(property.properties)) {
        const value = this.resolveProperty(childProperty, context, childKey);

        if (this.hasValue(value)) {
          result[childKey] = value;
        }
      }

      return Object.keys(result).length ? result : undefined;
    }

    const source = property['x-source'];

    if (!source) {
      return undefined;
    }

    let value = this.extractSourceValue(source.path, context);
    value = this.applyTransforms(value, source.transform, context, key);
    value = this.castValue(value, property.type);

    return value;
  }

  private resolveArrayProperty(properties: Record<string, any>, context: TransformContext): any[] {
    const columnValues: Record<string, any[]> = {};
    let maxLength = 0;

    for (const [key, property] of Object.entries(properties)) {
      const value = this.resolveProperty(property, context, key);

      if (Array.isArray(value)) {
        columnValues[key] = value;
        if (value.length > maxLength) {
          maxLength = value.length;
        }
      } else if (value !== undefined) {
        columnValues[key] = [value];
        if (maxLength < 1) {
          maxLength = 1;
        }
      }
    }

    const results: any[] = [];

    for (let index = 0; index < maxLength; index += 1) {
      const entry: Record<string, unknown> = {};

      for (const [key, values] of Object.entries(columnValues)) {
        const lineValue = values[index];

        if (this.hasValue(lineValue)) {
          entry[key] = lineValue;
        }
      }

      if (Object.keys(entry).length) {
        results.push(entry);
      }
    }

    return results;
  }

  private extractSourceValue(path: any, context: TransformContext): any {
    if (!path || (Array.isArray(path) && path.length === 0)) {
      return undefined;
    }

    if (Array.isArray(path) && Array.isArray(path[0])) {
      return path.map((segment) => this.navigatePath(context.working, segment));
    }

    return this.navigatePath(context.working, path);
  }

  private navigatePath(source: any, segments: any): any {
    if (!Array.isArray(segments)) {
      return undefined;
    }

    let results: any[] = [source];

    for (const segment of segments) {
      if (segment === '*') {
        results = results.flatMap((value) => (Array.isArray(value) ? value : []));
        continue;
      }

      const mapped = results.map((value) => this.getProperty(value, segment));
      results = mapped;
    }

    if (results.length === 0) {
      return undefined;
    }

    if (results.length === 1) {
      return results[0];
    }

    return results;
  }

  private getProperty(source: any, segment: any): any {
    if (source === undefined || source === null) {
      return undefined;
    }

    if (typeof segment === 'number') {
      return Array.isArray(source) ? source[segment] : undefined;
    }

    const numericIndex = Number(segment);

    if (!Number.isNaN(numericIndex) && Array.isArray(source)) {
      return source[numericIndex];
    }

    return source?.[segment];
  }

  private applyTransforms(
    value: any,
    transforms: any,
    context: TransformContext,
    key: string
  ): any {
    if (!transforms || !Array.isArray(transforms) || !transforms.length) {
      return value;
    }

    let current = value;

    for (const step of transforms) {
      if (current === undefined || current === null) {
        return current;
      }

      if (Array.isArray(step)) {
        const [name, ...args] = step;
        current = this.invokeTransform(name, current, args, context, key);
      } else {
        current = this.invokeTransform(step, current, [], context, key);
      }
    }

    return current;
  }

  private invokeTransform(
    name: string,
    value: any,
    args: any[],
    context: TransformContext,
    key: string
  ): any {
    const handler = this.transformHandlers[name];

    if (!handler) {
      throw new Error(`QDRS transform '${name}' is not implemented.`);
    }

    return handler(value, args, context, key);
  }

  private castValue(value: any, type?: string): any {
    if (value === undefined || value === null) {
      return value;
    }

    if (type === 'number') {
      const convert = (input: any) => {
        if (input === undefined || input === null || input === '') {
          return undefined;
        }

        const parsed = Number(input);

        return Number.isNaN(parsed) ? undefined : parsed;
      };

      if (Array.isArray(value)) {
        return value.map(convert);
      }

      return convert(value);
    }

    if (type === 'boolean') {
      const convert = (input: any) => Boolean(input);

      if (Array.isArray(value)) {
        return value.map(convert);
      }

      return convert(value);
    }

    return value;
  }

  private hasValue(value: any): boolean {
    if (value === undefined || value === null) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.some((entry) => this.hasValue(entry));
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  }

  private mapValue<T, R>(value: T | T[], mapper: (input: T) => R): R | R[] {
    if (Array.isArray(value)) {
      return (value as T[]).map((entry) => mapper(entry));
    }

    return mapper(value as T);
  }

  private normaliseString(value: any): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      if (value.value !== undefined && typeof value.value !== 'object') {
        return String(value.value);
      }

      if (typeof value.name === 'string' && value.name.trim().length) {
        return value.name as string;
      }

      if (typeof value.label === 'string' && value.label.trim().length) {
        return value.label as string;
      }

      if (typeof value.type === 'string' && value.type.trim().length) {
        return value.type as string;
      }
    }

    return undefined;
  }

  private extractStateValue(value: any): string | undefined {
    if (!value) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      if (typeof value.value === 'string') {
        return value.value;
      }

      if (typeof value.name === 'string') {
        return value.name;
      }
    }

    return undefined;
  }

  private unwrapOptionValue(value: any): any {
    if (!value) {
      return undefined;
    }

    if (value.value && typeof value.value === 'object') {
      return value.value;
    }

    return value;
  }

  private readFacilityField(source: any, field?: string): string | undefined {
    const data = this.unwrapOptionValue(source);

    if (!data) {
      return undefined;
    }

    if (!field) {
      return this.normaliseString(data);
    }

    switch (field) {
      case 'name':
        return (
          this.normaliseString(data.name ?? data.place ?? data.description ?? data.label) ??
          this.normaliseString(data)
        );
      case 'street':
        return this.normaliseString(data.street ?? data.address?.street);
      case 'suburb':
        return this.normaliseString(data.suburb ?? data.address?.suburb);
      case 'postcode':
        return this.normaliseString(data.postcode ?? data.address?.postcode);
      case 'state':
        return this.extractStateValue(data.state ?? data.address?.state);
      case 'country':
        return this.normaliseString(data.country ?? data.address?.country);
      default:
        return this.normaliseString(data[field] ?? data.address?.[field]);
    }
  }
  private qldToStillBornEnum(value: any): Stillborn | undefined {
    if (!value) {
      return undefined;
    }

    const normalised = this.normaliseString(value)?.toLowerCase();

    if (normalised === 'yes') {
      return Stillborn.Stillborn;
    }

    if (normalised === 'neonate') {
      return Stillborn.Neonate;
    }

    return Stillborn.AnyOtherDeceasedPerson;
  }

  private qdrsIsSingleName(name: any): boolean {
    if (!name) {
      return false;
    }

    const first = name.first ?? '';
    const last = name.last ?? '';

    return !first || !last;
  }

  private isTruthy(value: any): boolean {
    if (Array.isArray(value)) {
      return value.some((entry) => this.isTruthy(entry));
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return Boolean(value);
  }

  private conductorToQdrsGender(value: any): DeceasedSex | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    const mapped = this.genderMap.get(raw.toLowerCase());

    if (mapped) {
      return mapped;
    }

    return DeceasedSex.AnotherTerm;
  }

  private conductorToQdrsGenderOtherTerm(value: any): string | undefined {
    const raw = this.normaliseString(value);
    const gender = this.conductorToQdrsGender(value);

    if (gender === DeceasedSex.AnotherTerm) {
      return raw ?? undefined;
    }

    return undefined;
  }

  private conductorToQdrsIndigenousHeritage(value: any): string | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    return this.indigenousHeritageMap.get(raw.toLowerCase());
  }

  private conductorToQdrsDateOfPassingType(
    value: any
  ): DeceasedDateOfDeathKnownRegistration | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    return (
      this.dateOfPassingTypeMap.get(raw.toLowerCase()) ?? DeceasedDateOfDeathKnownRegistration.Known
    );
  }

  private toDateStr(value: any, inputFormat: string, outputFormat: string): any {
    const convert = (input: any) => {
      if (!input) {
        return undefined;
      }

      const date = moment(input, [outputFormat, inputFormat, moment.ISO_8601], true);

      if (!date.isValid()) {
        return undefined;
      }

      return date.format(outputFormat);
    };

    return this.mapValue(value, convert);
  }

  private conductorToQdrsAgeByUnit(value: any, unit: string): any {
    return this.conductorToQdrsAgeByUnitNumber(value, unit);
  }

  private conductorToQdrsAgeByUnitNumber(value: any, unit: string): any {
    const convert = (input: any) => {
      if (!Array.isArray(input)) {
        return undefined;
      }

      const [age, ageUnit] = input;

      if (age === undefined || age === null) {
        return undefined;
      }

      if (String(ageUnit).toLowerCase() === unit.toLowerCase()) {
        return age;
      }

      return undefined;
    };

    if (Array.isArray(value) && value.length === 2 && !Array.isArray(value[0])) {
      return convert(value);
    }

    if (Array.isArray(value) && Array.isArray(value[0])) {
      return value.map((entry) => convert(entry));
    }

    return convert(value);
  }

  private conductorToQdrsAgeByUnitString(value: any, unit: string): any {
    const result = this.conductorToQdrsAgeByUnitNumber(value, unit);

    if (Array.isArray(result)) {
      return result.map((entry) =>
        entry === undefined || entry === null ? undefined : String(entry)
      );
    }

    if (result === undefined || result === null) {
      return undefined;
    }

    return String(result);
  }

  private conductorToQdrsPlaceOfDeathType(value: any): DeceasedPlaceOfDeathType | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    return this.placeOfPassingTypeMap.get(raw.toLowerCase()) ?? DeceasedPlaceOfDeathType.Anyother;
  }

  private conductorToQdrsPlaceOfDeath(
    value: any,
    expected: string,
    field?: string,
    context?: TransformContext
  ): any {
    const [typeValue, facilityValue] = Array.isArray(value) ? value : [undefined, undefined];
    const placeType = this.normaliseString(typeValue)?.toLowerCase();
    const facility = this.unwrapOptionValue(facilityValue);
    const facilityType = this.normaliseString(facility?.type)?.toLowerCase();
    const target = expected.toLowerCase();

    const readFromFacility = (source: any) => this.readFacilityField(source, field);
    const residenceAddress = context?.arrangement?.deceased?.placeOfPassing?.address;

    switch (target) {
      case 'hospital':
        if (placeType?.endsWith('hospital')) {
          return readFromFacility(facility);
        }
        break;
      case 'nursing-home':
        if (placeType?.includes('nursing-home')) {
          return readFromFacility(facility);
        }
        break;
      case 'other-hospital':
        if (facilityType === 'other-hospital') {
          return readFromFacility(facility);
        }
        break;
      case 'other-nursing-home':
        if (facilityType === 'other-nursing-home') {
          return readFromFacility(facility);
        }
        break;
      case 'residence':
        if (
          placeType === 'usual-residence' ||
          placeType === 'none' ||
          facilityType === 'residence'
        ) {
          return readFromFacility(facility ?? residenceAddress);
        }
        return readFromFacility(residenceAddress);
      default:
        return readFromFacility(facility);
    }

    return undefined;
  }

  private conductorToQdrsPlaceOfDeathIsResidence(value: any): boolean {
    const raw = this.normaliseString(value)?.toLowerCase();

    return raw === 'usual-residence';
  }

  private getAddressPlaceName(value: any): string | undefined {
    const data = this.unwrapOptionValue(value);

    if (!data) {
      return undefined;
    }

    return this.normaliseString(data.place ?? data.name ?? data.description);
  }

  private join(value: any, separator = ' '): any {
    if (!Array.isArray(value)) {
      return value;
    }

    const cleaned = value
      .map((entry) => (entry === undefined || entry === null ? '' : String(entry)))
      .filter((entry) => entry.trim().length > 0);

    return cleaned.length ? cleaned.join(separator) : undefined;
  }

  private toBoolean(value: any, truthy: string, falsy?: string): boolean | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const normalised = String(value).toLowerCase();

    if (normalised === String(truthy).toLowerCase()) {
      return true;
    }

    if (falsy && normalised === String(falsy).toLowerCase()) {
      return false;
    }

    return Boolean(value);
  }

  private conductorToQdrsPlaceOfBirthKnown(value: any): DeceasedPlaceOfBirthKnown {
    if (!value || Object.keys(value).length === 0) {
      return DeceasedPlaceOfBirthKnown.Unknown;
    }

    if (!value.country && !value.suburb && !value.state) {
      return DeceasedPlaceOfBirthKnown.NotStated;
    }

    return DeceasedPlaceOfBirthKnown.Yes;
  }

  private isNotEqual(value: any, comparison: string): any {
    const filter = (input: any) => {
      if (input === undefined || input === null) {
        return undefined;
      }

      return String(input).toLowerCase() !== String(comparison).toLowerCase() ? input : undefined;
    };

    if (Array.isArray(value)) {
      return value.map(filter);
    }

    return filter(value);
  }

  private conductorToQdrsYearOfArrivalToAustralia(
    value: any
  ): DeaceasedYearOfArrivalAustralia | undefined {
    const raw = Array.isArray(value) ? value[0] : value;

    if (raw === true || this.normaliseString(raw)?.toLowerCase() === 'yes') {
      return DeaceasedYearOfArrivalAustralia.Known;
    }

    if (raw === false || this.normaliseString(raw)?.toLowerCase() === 'no') {
      return DeaceasedYearOfArrivalAustralia.Unknown;
    }

    return DeaceasedYearOfArrivalAustralia.NotStated;
  }

  private isAustralianPlaceOfBirth(value: any): string | undefined {
    if (!Array.isArray(value) || value.length < 2) {
      return undefined;
    }

    const [country, state] = value;
    const countryValue = this.normaliseString(country)?.toLowerCase();

    if (countryValue === 'australia') {
      return this.normaliseString(state) ?? undefined;
    }

    return undefined;
  }

  private isNotAustralianPlaceOfBirth(value: any): string | undefined {
    if (!Array.isArray(value) || value.length < 2) {
      return undefined;
    }

    const [country, state] = value;
    const countryValue = this.normaliseString(country)?.toLowerCase();

    if (countryValue && countryValue !== 'australia') {
      return this.normaliseString(state) ?? undefined;
    }

    return undefined;
  }

  private getLength(value: any): number | undefined {
    if (!value) {
      return undefined;
    }

    return Array.isArray(value) ? value.length : undefined;
  }

  private toMarriageEnum(value: any): MarriageType | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    switch (raw.toUpperCase()) {
      case 'MARRIED':
        return MarriageType.Marriage;
      case 'DE FACTO':
      case 'DEFACTO':
        return MarriageType.DeFactoRelationship;
      case 'RELATIONSHIP':
        return MarriageType.Relationship;
      default:
        return MarriageType.Relationship;
    }
  }

  private isEqual(value: any, comparison: string): any {
    const filter = (input: any) => {
      if (input === undefined || input === null) {
        return undefined;
      }

      return String(input).toLowerCase() === String(comparison).toLowerCase() ? input : undefined;
    };

    if (Array.isArray(value)) {
      return value.map(filter);
    }

    return filter(value);
  }

  private returnValue(value: any): any {
    return value;
  }

  private isMarriageAgeKnown(value: any): boolean {
    if (!Array.isArray(value) || value.length < 2) {
      return false;
    }

    const [birthDate, marriageYear] = value;

    return Boolean(birthDate) && Boolean(marriageYear);
  }

  private calculateMarriageAge(value: any, context: TransformContext): number | undefined {
    if (!Array.isArray(value) || value.length < 2) {
      return undefined;
    }

    const [birthDate, marriageYear] = value;

    if (!birthDate || !marriageYear) {
      return undefined;
    }

    const birth = moment(birthDate);
    const year = Number(marriageYear);

    if (!birth.isValid() || Number.isNaN(year)) {
      return undefined;
    }

    const marriageDate = moment({ year, month: birth.month(), day: birth.date() });

    if (!marriageDate.isValid()) {
      return undefined;
    }

    return marriageDate.diff(birth, 'years');
  }

  private toMarriageAgeQualifier(value: any): MarriageAgeQualifier {
    return value !== undefined && value !== null
      ? MarriageAgeQualifier.NotStated
      : MarriageAgeQualifier.Unknown;
  }

  private contact(value: any, path?: any, context?: TransformContext): any {
    const resolveContact = (entry: any) => {
      if (!entry) {
        return undefined;
      }

      let contact: any = entry;

      if (entry.id && context?.contacts?.has(entry.id)) {
        contact = context.contacts.get(entry.id);
      } else if (entry.child?.id && context?.contacts?.has(entry.child.id)) {
        contact = context.contacts.get(entry.child.id);
      } else if (entry.parent?.id && context?.contacts?.has(entry.parent.id)) {
        contact = context.contacts.get(entry.parent.id);
      }

      if (path) {
        return this.navigatePath(contact, Array.isArray(path) ? path : [path]);
      }

      return contact;
    };

    if (Array.isArray(value)) {
      return value.map(resolveContact);
    }

    return resolveContact(value);
  }

  private contactOverview(value: any): string | undefined {
    const describe = (contact: any) => {
      if (!contact) {
        return undefined;
      }

      const parts: string[] = [];

      const name = [contact.name?.first, contact.name?.last].filter(Boolean).join(' ');
      if (name) {
        parts.push(name);
      }

      if (contact.phone?.primary) {
        parts.push(contact.phone.primary);
      }

      if (contact.email?.primary) {
        parts.push(contact.email.primary);
      }

      return parts.join(' - ');
    };

    if (Array.isArray(value)) {
      const result = value.map(describe).filter((entry) => !!entry);
      return result.length ? result.join(', ') : undefined;
    }

    return describe(value);
  }

  private toLifeStatusEnum(value: any): ChildLifeStatus | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    switch (raw.toLowerCase()) {
      case 'alive':
        return ChildLifeStatus.Alive;
      case 'stillborn':
        return ChildLifeStatus.Stillborn;
      case 'deceased':
        return ChildLifeStatus.Deceased;
      default:
        return undefined;
    }
  }

  private disposal(value: any): any {
    if (!value) {
      return undefined;
    }

    const type = value.type?.value ?? value.type;
    const normalised = this.normaliseString(type)?.toLowerCase();

    if (normalised?.includes('burial')) {
      return value.burial ?? value;
    }

    if (normalised?.includes('crem')) {
      return value.cremation ?? value;
    }

    if (normalised?.includes('repatri')) {
      return value.repatriated ?? value;
    }

    if (normalised?.includes('donation')) {
      return value.bodyDonation ?? value;
    }

    return value;
  }

  private get(value: any, path: any): any {
    if (Array.isArray(value)) {
      return value.map((entry) => this.navigatePath(entry, Array.isArray(path) ? path : [path]));
    }

    return this.navigatePath(value, Array.isArray(path) ? path : [path]);
  }

  private getDisposalPlace(value: any): string | undefined {
    if (!value) {
      return undefined;
    }

    const address = value.address ?? value.crematoriumAddress ?? value;

    const parts = [
      this.normaliseString(address?.place ?? address?.name),
      this.normaliseString(address?.street),
      this.normaliseString(address?.suburb),
      this.extractStateValue(address?.state),
      this.normaliseString(address?.postcode),
    ].filter((part) => part);

    return parts.length ? parts.join(', ') : undefined;
  }

  private toDisposalTypeEnum(value: any): DeathDisposalType | undefined {
    const raw = this.normaliseString(value?.value ?? value);

    if (!raw) {
      return undefined;
    }

    const normalised = raw.toLowerCase();

    if (normalised.includes('burial')) {
      return DeathDisposalType.Burial;
    }

    if (normalised.includes('crem')) {
      return DeathDisposalType.Cremation;
    }

    if (normalised.includes('repatri')) {
      return DeathDisposalType.OutsideQueensland;
    }

    if (normalised.includes('donation')) {
      return DeathDisposalType.SchoolOfAnatomy;
    }

    return DeathDisposalType.Other;
  }

  private toDisposalOutsideEnum(value: any): DisposalOutsideMethod | undefined {
    const type = this.normaliseString(value?.type?.value ?? value?.type ?? value);
    const normalised = type?.toLowerCase();

    if (normalised?.includes('burial')) {
      return DisposalOutsideMethod.Burial;
    }

    if (normalised?.includes('crem')) {
      return DisposalOutsideMethod.Cremation;
    }

    if (normalised?.includes('other')) {
      return DisposalOutsideMethod.Other;
    }

    return undefined;
  }

  private isDisposalOutsideQld(value: any): any {
    const type = this.normaliseString(value?.type?.value ?? value?.type);

    if (!type) {
      return undefined;
    }

    const normalised = type.toLowerCase();

    if (normalised.includes('repatri')) {
      return value;
    }

    return undefined;
  }

  private calculateAge(value: any, context: TransformContext): number | number[] | undefined {
    const convert = (input: any) => {
      if (!input) {
        return undefined;
      }

      const passingDate = context.arrangement?.deceased?.dateOfPassing;
      const reference = passingDate ? moment(passingDate) : moment();
      const birth = moment(input);

      if (!birth.isValid() || !reference.isValid()) {
        return undefined;
      }

      return reference.diff(birth, 'years');
    };

    const mapped = this.mapValue(value, convert);

    if (Array.isArray(mapped)) {
      const filtered = (mapped as Array<number | undefined>).filter(
        (entry): entry is number => entry !== undefined
      );

      return filtered.length ? filtered : undefined;
    }

    return mapped ?? undefined;
  }

  private toAgeUnit(value: any, context: TransformContext): string | string[] | undefined {
    const convert = (input: any) => {
      if (!input) {
        return undefined;
      }

      const passingDate = context.arrangement?.deceased?.dateOfPassing;
      const reference = passingDate ? moment(passingDate) : moment();
      const birth = moment(input);

      if (!birth.isValid() || !reference.isValid()) {
        return undefined;
      }

      const years = reference.diff(birth, 'years');
      if (years >= 1) {
        return 'years';
      }

      const months = reference.diff(birth, 'months');
      if (months >= 1) {
        return 'months';
      }

      const days = reference.diff(birth, 'days');
      if (days >= 1) {
        return 'days';
      }

      const hours = reference.diff(birth, 'hours');
      if (hours >= 1) {
        return 'hours';
      }

      const minutes = reference.diff(birth, 'minutes');
      if (minutes >= 1) {
        return 'minutes';
      }

      return 'seconds';
    };

    const mapped = this.mapValue(value, convert);

    if (Array.isArray(mapped)) {
      const filtered = (mapped as Array<string | undefined>).filter(
        (entry): entry is string => !!entry
      );

      return filtered.length ? filtered : undefined;
    }

    return mapped ?? undefined;
  }

  private toParentTypeEnum(value: any): string | undefined {
    const raw = this.normaliseString(value);

    if (!raw) {
      return undefined;
    }

    const normalised = raw.toLowerCase();

    if (normalised.includes('father')) {
      return DeceasedParentIndicator.Father;
    }

    if (normalised.includes('mother')) {
      return DeceasedParentIndicator.Mother;
    }

    if (normalised.includes('parent')) {
      return DeceasedParentIndicator.Parent;
    }

    return DeceasedParentIndicator.NotStated;
  }

  private log(value: any, message: string): any {
    console.warn('[QDRS]', message);
    return value;
  }
}
